import { useState } from 'react'
import { ArrowDown, ArrowUp, Eye, EyeOff } from 'lucide-react'
import type { ContactFieldLayoutEntry } from '@/api/agency'
import { useAgencyQuery, useUpdateAgency } from '@/features/agency/useAgency'
import { apiErrorMessage } from './apiErrorMessage'

/**
 * The built-in contact fields that can be reordered / hidden on the contact detail, in default
 * order. Consent fields (do-not-email/call/sms) are intentionally excluded: they now live in an
 * always-visible "Communication preferences" block so a contact can always be re-subscribed.
 */
const CATALOG: { key: string; label: string; hideable: boolean }[] = [
  { key: 'first_name', label: 'First name', hideable: false },
  { key: 'last_name', label: 'Last name', hideable: true },
  { key: 'email', label: 'Email', hideable: false },
  { key: 'phone', label: 'Phone', hideable: true },
  { key: 'mobile', label: 'Mobile', hideable: true },
  { key: 'owner', label: 'Owner', hideable: true },
  { key: 'lead_source', label: 'Lead source', hideable: true },
  { key: 'birthday', label: 'Birthday', hideable: true },
]

interface Row {
  key: string
  label: string
  defaultLabel: string
  hidden: boolean
  hideable: boolean
}

/** Merge the saved layout over the catalog: listed keys first, then remaining defaults. */
function mergeLayout(layout: ContactFieldLayoutEntry[]): Row[] {
  const byKey = new Map(layout.map((l) => [l.key, l]))
  const orderedKeys = [
    ...layout.map((l) => l.key).filter((k) => CATALOG.some((c) => c.key === k)),
    ...CATALOG.map((c) => c.key).filter((k) => !byKey.has(k)),
  ]
  return orderedKeys.map((k) => {
    const c = CATALOG.find((x) => x.key === k) as (typeof CATALOG)[number]
    const e = byKey.get(k)
    return {
      key: k,
      label: (e?.label ?? '').trim() || c.label,
      defaultLabel: c.label,
      hidden: c.hideable ? Boolean(e?.hidden) : false,
      hideable: c.hideable,
    }
  })
}

export default function ContactFormTab() {
  const agencyQuery = useAgencyQuery()
  const update = useUpdateAgency()
  const [draft, setDraft] = useState<Row[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const rowsOrNull = draft ?? (agencyQuery.data ? mergeLayout(agencyQuery.data.contactFieldLayout) : null)

  if (!rowsOrNull) {
    return <p className="text-sm text-gray-400">Loading…</p>
  }
  const rows: Row[] = rowsOrNull

  function edit(next: Row[]) {
    setDraft(next)
    setError(null)
  }
  function move(i: number, dir: -1 | 1) {
    const j = i + dir
    if (j < 0 || j >= rows.length) return
    const next = [...rows]
    ;[next[i], next[j]] = [next[j], next[i]]
    edit(next)
  }
  function toggleHidden(i: number) {
    if (!rows[i].hideable) return
    edit(rows.map((r, k) => (k === i ? { ...r, hidden: !r.hidden } : r)))
  }
  function relabel(i: number, label: string) {
    edit(rows.map((r, k) => (k === i ? { ...r, label } : r)))
  }

  function save() {
    const layout: ContactFieldLayoutEntry[] = rows.map((r) => ({
      key: r.key,
      hidden: r.hidden,
      // Only store a label when it differs from the default, so default-label changes still propagate.
      ...(r.label.trim() && r.label.trim() !== r.defaultLabel ? { label: r.label.trim() } : {}),
    }))
    const a = agencyQuery.data
    // Pass brand fields through untouched: the backend replaces the whole record and only
    // treats currency/roles/lead-source/layout as "null = keep", so name/brand/logo must be sent.
    update.mutate(
      {
        name: a?.name ?? '',
        brandColor: a?.brandColor ?? null,
        brandColorDark: a?.brandColorDark ?? null,
        brandColor50: a?.brandColor50 ?? null,
        logoUrl: a?.logoUrl ?? null,
        contactFieldLayout: layout,
      },
      { onSuccess: () => setDraft(null), onError: (e) => setError(apiErrorMessage(e)) },
    )
  }

  return (
    <div className="max-w-3xl">

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {rows.map((r, i) => (
          <div
            key={r.key}
            className={`px-4 py-2.5 border-t border-gray-100 first:border-t-0 flex items-center gap-2 ${
              r.hidden ? 'opacity-50' : ''
            }`}
          >
            <div className="flex flex-col leading-none">
              <button
                onClick={() => move(i, -1)}
                disabled={i === 0}
                className="text-gray-300 hover:text-brand-primary disabled:opacity-30"
                title="Move up"
              >
                <ArrowUp size={12} />
              </button>
              <button
                onClick={() => move(i, 1)}
                disabled={i === rows.length - 1}
                className="text-gray-300 hover:text-brand-primary disabled:opacity-30"
                title="Move down"
              >
                <ArrowDown size={12} />
              </button>
            </div>
            <input
              value={r.label}
              onChange={(e) => relabel(i, e.target.value)}
              className="flex-1 px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
            />
            <span className="text-[11px] text-gray-400 font-mono w-28 truncate">{r.key}</span>
            <button
              onClick={() => toggleHidden(i)}
              disabled={!r.hideable}
              className="text-gray-400 hover:text-brand-primary disabled:opacity-30"
              title={r.hideable ? (r.hidden ? 'Show on form' : 'Hide from form') : 'Always shown'}
            >
              {r.hidden ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        ))}
      </div>

      {error && <p className="text-danger text-xs mt-3">{error}</p>}

      <div className="flex items-center gap-3 mt-4">
        <button
          onClick={save}
          disabled={update.isPending || !draft}
          className="px-4 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark disabled:opacity-60"
        >
          {update.isPending ? 'Saving…' : 'Save'}
        </button>
        {draft && (
          <button onClick={() => setDraft(null)} className="text-xs text-gray-500 hover:text-brand-dark">
            Reset
          </button>
        )}
        {update.isSuccess && !draft && <span className="text-xs text-success">Saved ✓</span>}
      </div>
    </div>
  )
}
